import { BatteryCharging, BatteryLow, PiggyBank } from "lucide-react";
import { Link } from "react-router-dom";
import { useSnapshot } from "@/store/site";
import { formatEur, formatKWh } from "@/lib/format";
import { useDischargePrice } from "@/hooks/useDischargePrice";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";

/**
 * Two headline tiles on the Overview. Show ONLY values that come from a
 * real EMS-server source; missing values render as "—" with a short hint
 * about what's needed to populate them.
 *
 *   Money saved today = (today's BESS discharge in kWh) × (operator-set
 *   discharge price in €/kWh). The price is the site attribute
 *   `ems_discharge_price_eur_kwh`, edited from the Revenue page —
 *   reusing the same source the BessSavingsBrowser uses keeps every
 *   savings figure on the site computed against one tariff.
 *
 *   Battery cycled today = today's charged kWh + today's discharged
 *   kWh, sourced from the per-BESS EP_d / EN_d counters via the
 *   adapter's daily-totals refresh.
 */
export function StoryCards() {
  const snap = useSnapshot();
  const today = snap.energy_today;
  const t = useT();
  const { price: dischargePrice, isReady: priceReady } = useDischargePrice();

  const charge_kWh = Number.isFinite(today.bess_charge_kWh) ? today.bess_charge_kWh : NaN;
  const discharge_kWh = Number.isFinite(today.bess_discharge_kWh) ? today.bess_discharge_kWh : NaN;
  const throughputKnown = Number.isFinite(charge_kWh) && Number.isFinite(discharge_kWh);
  const throughput_kWh = throughputKnown ? charge_kWh + discharge_kWh : 0;

  const moneyKnown = priceReady && dischargePrice > 0 && Number.isFinite(discharge_kWh);
  const moneySaved = moneyKnown ? discharge_kWh * dischargePrice : 0;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* Money card drills into /revenue — where the discharge price is
          set/edited, the affordance its own empty-state text asks for. */}
      <Story
        tone="export"
        icon={PiggyBank}
        label={t("monitor.story.moneySaved")}
        value={moneyKnown ? formatEur(moneySaved) : "—"}
        sub={moneyKnown
          ? t("monitor.story.moneySub", { kwh: formatKWh(discharge_kWh, 0), price: dischargePrice.toFixed(3) })
          : priceReady
            ? t("monitor.story.setPrice")
            : t("monitor.story.loadingPrice")}
        to={carryTo("/revenue")}
      />

      {/* Throughput card drills into /historical — the curated chart plots
          BESS power (charge/discharge) over the same window (OVW-06). */}
      <Story
        tone={discharge_kWh > charge_kWh ? "discharge" : "charge"}
        icon={discharge_kWh > charge_kWh ? BatteryLow : BatteryCharging}
        label={t("monitor.story.cycled")}
        value={throughputKnown ? `${formatKWh(throughput_kWh, 0)} kWh` : "—"}
        sub={throughputKnown
          ? t("monitor.story.cycledSub", { c: formatKWh(charge_kWh, 0), d: formatKWh(discharge_kWh, 0) })
          : t("monitor.story.waitingCounters")}
        to={carryTo("/historical")}
      />
    </div>
  );
}

function Story({
  tone, icon: Icon, label, value, sub, to,
}: {
  tone: "export" | "charge" | "discharge" | "solar";
  icon: any;
  label: string;
  value: string;
  sub: string;
  /** Optional drill-down route — renders the card as a Link (BessTile hover treatment). */
  to?: string;
}) {
  // Glow tracks the matching --ems-* token via color-mix so the corner tint
  // theme-switches with the rest of the card (the old hardcoded RGBAs were
  // frozen to the LIGHT palette — charge was even the retired royal blue).
  const tints = {
    export:    { num: "text-export",    icon: "bg-export/15 text-export",       glow: "color-mix(in srgb, var(--ems-grid-export) 16%, transparent)" },
    charge:    { num: "text-charge",    icon: "bg-charge/15 text-charge",       glow: "color-mix(in srgb, var(--ems-charge) 16%, transparent)"      },
    discharge: { num: "text-discharge", icon: "bg-discharge/15 text-discharge", glow: "color-mix(in srgb, var(--ems-discharge) 16%, transparent)"   },
    solar:     { num: "text-solar",     icon: "bg-solar/15 text-solar",         glow: "color-mix(in srgb, var(--ems-solar) 16%, transparent)"       },
  }[tone];
  const body = (
    <>
      <div className="story-tint pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(circle at top right, ${tints.glow} 0%, transparent 60%)` }} />
      <div className={cn("inline-grid h-9 w-9 place-items-center rounded-md", tints.icon)}>
        <Icon size={18} strokeWidth={1.7} />
      </div>
      <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-text-faint">
        {label}
      </div>
      <div className={cn("mt-1 text-3xl font-bold leading-none", tints.num, "num")}>{value}</div>
      <div className="mt-1.5 text-xs text-text-muted">{sub}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="card card-hover relative block overflow-hidden p-5 transition">
        {body}
      </Link>
    );
  }
  return (
    <div className="card relative overflow-hidden p-5">
      {body}
    </div>
  );
}
